import React, { createContext, useContext, useState, useCallback, useRef } from "react";

type ToastPosition = "top-right" | "top-left" | "bottom-right" | "bottom-left";

interface ShowToastOptions {
    message: string;
    type?: string;
    /** Duration in ms. Pass 0 for a persistent toast that stays until hideToast() is called. */
    duration?: number;
    position?: ToastPosition;
}

interface ToastContextValue {
    showToast: (opts: ShowToastOptions) => void;
    /** Programmatically dismiss the current toast (use for persistent toasts on recovery). */
    hideToast: () => void;
}

const ToastContext = createContext<ToastContextValue>({
    showToast: () => {},
    hideToast: () => {},
});

export const useToast = () => useContext(ToastContext);

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [toast, setToast] = useState<{ message: string; type: string; position: ToastPosition } | null>(null);
    const autoDismissRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const hideToast = useCallback(() => {
        if (autoDismissRef.current) {
            clearTimeout(autoDismissRef.current);
            autoDismissRef.current = null;
        }
        setToast(null);
    }, []);

    const showToast = useCallback(
        ({
            message,
            type = "success",
            duration = 3000,
            position = "top-right",
        }: ShowToastOptions) => {
            // Cancel any previous auto-dismiss before showing new toast
            if (autoDismissRef.current) {
                clearTimeout(autoDismissRef.current);
                autoDismissRef.current = null;
            }

            setToast({ message, type, position });

            // duration === 0 → persistent; caller must call hideToast() to dismiss
            if (duration > 0) {
                autoDismissRef.current = setTimeout(() => {
                    setToast(null);
                    autoDismissRef.current = null;
                }, duration);
            }
        },
        [],
    );

    return (
        <ToastContext.Provider value={{ showToast, hideToast }}>
            {children}
            {toast && <Toast message={toast.message} type={toast.type} position={toast.position} />}
        </ToastContext.Provider>
    );
};

interface ToastProps {
    message: string;
    type: string;
    position: ToastPosition;
}

const Toast = ({ message, type, position }: ToastProps) => {
    const bgColor =
        type === "success" ? "bg-green-500" :
        type === "warning" ? "bg-yellow-500" :
        "bg-red-500";

    const positionClass = {
        "top-right": "top-4 right-4",
        "top-left": "top-4 left-4",
        "bottom-right": "bottom-4 right-4",
        "bottom-left": "bottom-4 left-4",
    }[position];

    return (
        <div
            className={`absolute top-[65px] ${positionClass} px-4 py-2 rounded text-white shadow-lg z-50 ${bgColor} animate-slide-in`}
        >
            {message}
        </div>
    );
};
